import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useCancelRoomBooking, useRoomBookingsQuery } from "../api/queries";
import { CompleteRoomBookingDialog } from "../ui/CompleteRoomBookingDialog";
import { RoomBookingsFilter } from "../ui/RoomBookingsFilter";
import { RoomBookingsTable } from "../ui/RoomBookingsTable";
import { RoomBookingsToolbar } from "../ui/RoomBookingsToolbar";

export function RoomBookingsPage() {
  const nav = useNavigate({ from: "/app/room-bookings" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/room-bookings",
  });
  const [completeBookingId, setCompleteBookingId] = useState<string | null>(
    null,
  );

  const query = useMemo<OffsetPageQueryDTO>(
    () => ({
      limit: search.limit ?? 20,
      offset: search.offset ?? 0,
      sort: search.sort,
      filters: search.filters,
    }),
    [search.limit, search.offset, search.sort, search.filters],
  );

  const list = useRoomBookingsQuery(query);
  const canManage = useActionPermission(["room-bookings:create"]);
  const canUpdate = useActionPermission(["room-bookings:update"]);
  const cancelBooking = useCancelRoomBooking();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການຈອງຫ້ອງ" },
          ]}
        />
      </Header>

      <Main>
        <RoomBookingsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/room-bookings/new" })}
          onBulkCreate={() => nav({ to: "/app/room-bookings/bulk-create" })}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <RoomBookingsFilter />

          <RoomBookingsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort?.[0]?.field}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(booking) =>
              nav({
                to: "/app/room-bookings/$id",
                params: { id: booking.id },
              })
            }
            onEdit={(booking) =>
              nav({
                to: "/app/room-bookings/$id/edit",
                params: { id: booking.id },
              })
            }
            onComplete={
              canUpdate ? (id) => setCompleteBookingId(id) : undefined
            }
            onCancel={async (id) => {
              toast.promise(cancelBooking.mutateAsync(id), {
                loading: "ກຳລັງຍົກເລີກ...",
                success: "ຍົກເລີກການຈອງສຳເລັດ",
                error: "ຍົກເລີກການຈອງລົ້ມເຫຼວ",
              });
            }}
          />
        </div>
      </Main>

      <CompleteRoomBookingDialog
        bookingId={completeBookingId}
        open={!!completeBookingId}
        onOpenChange={(open) => {
          if (!open) setCompleteBookingId(null);
        }}
        onSuccess={() => {
          toast.success("ສຳເລັດການຈອງແລ້ວ");
        }}
      />
    </>
  );
}
